import { useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { 
  getUserOtherPrivilegesDetails, 
  UserOtherPrivilegesEnum, 
  UserOtherPrivilegesRequest,
  OtherPrivilegeDetail
} from '../modules/auth/userOtherPrivilegesSlice';

// ----------------------------------------------------------------------

export type ReturnType = {
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  hasOtherPrivilege: (uopmid: UserOtherPrivilegesEnum, privid: number) => boolean;
  getOtherPrivilege: (uopmid: UserOtherPrivilegesEnum, privid: number) => OtherPrivilegeDetail | undefined;
  userRoles: string[];
  loading: boolean;
  error: string | null;
};

export function useUserRole(): ReturnType {
  const dispatch = useAppDispatch();
  const userMenus = useAppSelector(state => state.userMenus);
  const userOtherPrivileges = useAppSelector(state => state.userOtherPrivileges);
  const { user, isAuthenticated } = useAppSelector(state => state.auth);

  // Kullanıcının sahip olduğu tüm rolleri al
  const userRoles = userMenus.data?.map(privilege => privilege.key_value?.toString()) || [];

  useEffect(() => {
    if (isAuthenticated && !userOtherPrivileges.loaded && !userOtherPrivileges.loading && !userOtherPrivileges.error) {
      const request: UserOtherPrivilegesRequest = {
        uopmids: [
          {
            uopmid: UserOtherPrivilegesEnum.MedicalCards,
            privids: []
          }
        ]
      };
      
      // Dispatch the action to fetch privileges
      dispatch(getUserOtherPrivilegesDetails(request));
    }
  }, [isAuthenticated, userOtherPrivileges.loaded, userOtherPrivileges.loading, userOtherPrivileges.error, dispatch]);

  const hasRole = useCallback((role: string) => {
    if (!role || userMenus.loading) return false;
    return userRoles.includes(role);
  }, [userRoles, userMenus.loading]);

  const hasAnyRole = useCallback((roles: string[]) => {
    if (!roles || roles.length === 0 || userMenus.loading) return false;
    return roles.some(role => hasRole(role));
  }, [hasRole, userMenus.loading]);

  const hasAllRoles = useCallback((roles: string[]) => {
    if (!roles || roles.length === 0 || userMenus.loading) return false;
    return roles.every(role => hasRole(role));
  }, [hasRole, userMenus.loading]);

  const hasOtherPrivilege = useCallback((uopmid: UserOtherPrivilegesEnum, privid: number) => {
     if (userOtherPrivileges.loading) return false;
     return userOtherPrivileges.data.some(p => p.uopmid === uopmid && p.privid === privid);
  }, [userOtherPrivileges.data, userOtherPrivileges.loading]);

  const getOtherPrivilege = useCallback((uopmid: UserOtherPrivilegesEnum, privid: number) => {
    if (userOtherPrivileges.loading) return undefined;
    return userOtherPrivileges.data.find(p => p.uopmid === uopmid && p.privid === privid);
 }, [userOtherPrivileges.data, userOtherPrivileges.loading]);

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasOtherPrivilege,
    getOtherPrivilege,
    userRoles,
    loading: userMenus.loading || userOtherPrivileges.loading,
    error: userMenus.error || userOtherPrivileges.error,
  };
}